import type { GameState } from '../state';
import { D, sub, mul, pow, max, gte, type Decimal } from '../math/num';
import {
  SWARM_COMPLETE,
  CORE_BOOST_PER_SAT,
  launchDurationMs,
  SAT_ALLOY_BASE,
  SAT_ALLOY_GROWTH,
  SAT_COMP_BASE,
  SAT_COMP_GROWTH,
} from '../data/harvest';

/**
 * Star Harvest sim (data/harvest.ts holds the numbers). One launch at a time
 * off elapsedMs; the satellite count is the only thing that matters.
 */

export interface HarvestOpResult {
  ok: boolean;
  reason?: string;
}

/** The multiplier the swarm feeds into the Mech Core's power output. */
export function harvestCoreBoost(state: GameState): number {
  if (!state.harvest.unlocked) return 1;
  return 1 + CORE_BOOST_PER_SAT * Math.min(SWARM_COMPLETE, state.harvest.satellites);
}

export function swarmComplete(state: GameState): boolean {
  return state.harvest.satellites >= SWARM_COMPLETE;
}

export function satelliteAlloyCost(state: GameState): Decimal {
  return mul(D(SAT_ALLOY_BASE), pow(D(SAT_ALLOY_GROWTH), state.harvest.satellites));
}

export function satelliteComponentCost(state: GameState): Decimal {
  const anchor = max(D(1), pow(D(1.08), state.combat.highestWave - 1));
  return mul(mul(D(SAT_COMP_BASE), pow(D(SAT_COMP_GROWTH), state.harvest.satellites)), anchor);
}

/** Resolve a finished launch (called from stepWorld). */
export function stepHarvest(state: GameState, _dtMs: number): void {
  const h = state.harvest;
  if (!h.unlocked || !h.launch) return;
  if (state.elapsedMs < h.launch.endsMs) return;
  h.launch = null;
  h.satellites = Math.min(SWARM_COMPLETE, h.satellites + 1);
}

export function launchSatellite(state: GameState): HarvestOpResult {
  const h = state.harvest;
  if (!h.unlocked) return { ok: false, reason: 'The star is not yours yet.' };
  if (swarmComplete(state)) return { ok: false, reason: 'The swarm is complete. Sol works for you now.' };
  if (h.launch) return { ok: false, reason: 'A launch is already on the pad.' };
  const alloy = satelliteAlloyCost(state);
  const comps = satelliteComponentCost(state);
  if (!gte(state.wallet.alloy, alloy)) return { ok: false, reason: 'Not enough Alloy (the Smelter feeds the pad).' };
  if (!gte(state.wallet.components, comps)) return { ok: false, reason: 'Not enough Components.' };
  state.wallet.alloy = sub(state.wallet.alloy, alloy);
  state.wallet.components = sub(state.wallet.components, comps);
  h.launch = { endsMs: state.elapsedMs + launchDurationMs(h.satellites) };
  return { ok: true };
}

/** Claim the star — rides the Sol kill. */
export function unlockHarvest(state: GameState): void {
  if (state.harvest.unlocked) return;
  state.harvest.unlocked = true;
  state.meta.unlocks.harvest = true;
}
